import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import useGuestParameters from '../../hooks/useGuestParameters';
import { useContent } from '../../contexts/ContentContext';
import useWeddingSlug from '../../hooks/useWeddingSlug';
import useRSVPSubmission from '../../hooks/useRSVPSubmission';
import { getPublicWishes } from '../../lib/api';
import BottomFloatingMenu from '../../components/ui/BottomFloatingMenu';
import RSVPForm from './components/RSVPForm';
import RSVPFormStepByStep from './components/RSVPFormStepByStep';
import EventDetailsCard from './components/EventDetailsCard';
import SuccessConfirmation from './components/SuccessConfirmation';
import Icon from '../../components/AppIcon';

const RSVPManagement = () => {
  const navigate = useNavigate();
  const { slug, weddingPath } = useWeddingSlug();
  const guestData = useGuestParameters();
  const { content } = useContent();
  const { isSubmitting, submitError, submittedData, showSuccess, handleSubmit } = useRSVPSubmission(slug);
  const [wishes, setWishes] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (slug) {
      getPublicWishes(slug).then(data => setWishes(data.wishes || [])).catch(() => {});
    }
  }, [slug]);

  const handleCloseSuccess = () => {
    navigate(`${weddingPath()}?guestId=${guestData?.guestId}&name=${encodeURIComponent(guestData?.guestName)}&type=${guestData?.invitationType}`);
  };

  if (guestData?.isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-base md:text-lg text-muted-foreground">Loading RSVP form...</p>
        </div>
      </div>
    );
  }

  if (guestData?.error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card rounded-xl p-6 md:p-8 shadow-romantic-md border border-border text-center">
          <div className="w-16 h-16 rounded-full bg-error/10 flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertCircle" size={32} color="var(--color-error)" />
          </div>
          <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
            Invalid Invitation Link
          </h2>
          <p className="text-sm md:text-base text-muted-foreground mb-6">
            {guestData?.error}. Please check your invitation link or contact the couple.
          </p>
          <button
            onClick={() => navigate(weddingPath())}
            className="text-primary hover:text-primary/80 font-medium transition-romantic"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>RSVP - {guestData?.coupleNames || 'Wedding Invitation'}</title>
        <meta name="description" content="Confirm your attendance for our special day. Please RSVP with your meal preferences and guest information." />
      </Helmet>
      <div className="min-h-screen bg-background pb-24">
        <header className="bg-card border-b border-border shadow-romantic-sm sticky top-0 z-50">
          <div className="max-w-screen-xl mx-auto px-4 md:px-6 lg:px-8 py-4 md:py-5">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate(`${weddingPath()}?guestId=${guestData?.guestId}&name=${encodeURIComponent(guestData?.guestName)}&type=${guestData?.invitationType}`)}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-romantic press-scale"
                aria-label="Back to invitation"
              >
                <Icon name="ArrowLeft" size={20} />
                <span className="text-sm md:text-base font-medium">Back</span>
              </button>
              <h1 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
                RSVP
              </h1>
              <div className="w-16" />
            </div>
          </div>
        </header>

        <main className="max-w-screen-xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="mb-6 md:mb-8 lg:mb-12 text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-3 md:mb-4">
              We Hope You Can Join Us
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              Please confirm your attendance by filling out the form below. We look forward to celebrating this special day with you!
            </p>
          </div>

          {submitError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-center">
              {submitError}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2 space-y-6 md:space-y-8">
              {content?.rsvp?.formStyle === 'step-by-step' ? (
                <RSVPFormStepByStep
                  guestData={guestData}
                  onSubmit={handleSubmit}
                  isSubmitting={isSubmitting}
                />
              ) : (
                <RSVPForm
                  guestData={guestData}
                  onSubmit={handleSubmit}
                  isSubmitting={isSubmitting}
                />
              )}
            </div>

            <div className="space-y-6 md:space-y-8">
              <EventDetailsCard
                coupleNames={guestData?.coupleNames}
                weddingDate={guestData?.weddingDate}
              />
            </div>
          </div>
        </main>

        {/* Wedding Wishes Wall */}
        {wishes.length > 0 && (
          <section className="max-w-screen-xl mx-auto px-4 md:px-6 lg:px-8 pb-8">
            <div className="text-center mb-6">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                <Icon name="MessageSquare" size={24} color="var(--color-primary)" />
              </div>
              <h2 className="text-2xl md:text-3xl font-semibold text-foreground">Wedding Wishes</h2>
              <p className="text-sm text-muted-foreground mt-1">Messages from our loved ones</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[600px] overflow-y-auto">
              {wishes.map((wish, idx) => (
                <div key={idx} className="bg-card rounded-xl p-4 border border-border shadow-romantic-sm">
                  <p className="text-sm text-foreground italic mb-3">"{wish.special_requests}"</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-primary">{wish.guest_name}</span>
                    <span className="text-xs text-muted-foreground">
                      {wish.created_at ? new Date(wish.created_at).toLocaleDateString() : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <BottomFloatingMenu />

        {showSuccess && submittedData && (
          <SuccessConfirmation
            guestName={submittedData.guestName}
            attending={submittedData.attending}
            attendingCeremony={submittedData.attendingCeremony}
            attendingReception={submittedData.attendingReception}
            numberOfGuests={submittedData.numberOfGuests}
            onClose={handleCloseSuccess}
          />
        )}
      </div>
    </>
  );
};

export default RSVPManagement;
